import {
  IStyles,
  ImageWrapper,
  SecondaryButton,
  deserify,
  formatSecondsToDateTimeString,
  pxToRem,
  pxTovW,
  theme,
  useCommonServiceClientContext,
} from '@geneo2-web/shared-ui';
import { Box, Typography, useMediaQuery } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { AssessmentEnum } from '@protos/learning_management/lms.db_pb';
import { ProfileRolesEnum } from '@protos/user_management/ums.db_pb';
import { AssessmentContent } from '@protos/learning_management/lms.assessment.common.apis_pb';
import { useWhiteLabeledUrl } from 'apps/teacher/src/utils/customHooks';
import { OMR_SHEET_PDF_URL } from 'apps/teacher/src/utils/commonVariables';
import {
  useAppDispatch,
  useAppSelector,
} from 'apps/teacher/src/reduxStore/reduxHooks';
import { useDownloadContext } from 'apps/teacher/src/app/Context/DownloadContextProviderV2';
import { useGlobalContext } from 'apps/teacher/src/app/Context/GlobalContextProvider';
import { setToastInfo } from '../../Home/reducer/homeDashboard.slice';
import {
  HOMEWORK_MANAGE,
  Project_Dashboard,
} from 'apps/teacher/src/routeHandling/RoutesNomenclature';
import { resetProjectFlow } from '../CreateProject/reducer/projectFlow.Slice';

const styles: IStyles = {
  root: {
    minHeight: { xs: '92vh', md: '94vh', lg: '84vh' },
    display: 'flex',
    background: '#F2F3FE',
    flexDirection: 'column',
    justifyContent: { xs: 'center', md: 'start', lg: 'center' },
    alignItems: 'center',
    gap: { xs: pxToRem(0), md: pxToRem(15), lg: pxTovW(15) },
    paddingLeft: { xs: pxToRem(18), md: pxToRem(21), lg: pxTovW(24) },
    paddingRight: { xs: pxToRem(18), md: pxToRem(21), lg: pxTovW(24) },
    paddingBottom: { xs: pxToRem(200), md: pxToRem(54), lg: pxTovW(80) },
  },
  correctImage: {
    paddingTop: { xs: pxToRem(0), md: pxToRem(30), lg: pxTovW(40) },
    height: { xs: pxToRem(88.12), md: pxToRem(78), lg: pxTovW(119) },
    width: { xs: pxToRem(156), md: pxToRem(150), lg: pxTovW(208) },
  },
  detailsStrip: {
    display: 'flex',
    height: { xs: 'max-content', md: 'max-content' },
    width: { xs: pxToRem(302), md: '90%', lg: pxTovW(971) },
    justifyContent: 'center',
    alignItems: 'center',
    gap: { xs: pxToRem(20), md: pxToRem(20), lg: pxTovW(40) },
    flexDirection: { xs: 'column', md: 'row' },
    // The tablet width is a share of the page, so it has to include padding.
    boxSizing: { md: 'border-box', lg: 'content-box' },
    border: '1px solid #3ACBFC',
    backgroundColor: '#E3F6FD',
    borderRadius: { xs: pxToRem(15), md: pxToRem(15), lg: pxTovW(15) },
    paddingLeft: { xs: pxToRem(18), md: pxToRem(21), lg: pxTovW(24) },
    paddingRight: { xs: pxToRem(18), md: pxToRem(21), lg: pxTovW(24) },
    paddingTop: { xs: pxToRem(10), md: pxToRem(26), lg: pxTovW(32) },
    paddingBottom: { xs: pxToRem(10), md: pxToRem(26), lg: pxTovW(32) },
    marginTop: { xs: pxToRem(20), md: pxToRem(17), lg: pxTovW(17) },
  },
  detailsColumn: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    borderRight: { md: '1px dashed #007CDC' },
    borderBottom: { xs: '1px dashed #007CDC', md: 'none' },
    paddingRight: { md: pxToRem(20), lg: pxTovW(40) },
    paddingBottom: { xs: pxToRem(20), md: '0px' },
    gap: { xs: pxToRem(15), md: pxToRem(0), lg: pxTovW(0) },
  },
  // Same column, minus the separator - nothing follows it.
  detailsLastColumn: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    paddingRight: { md: pxToRem(20), lg: pxTovW(40) },
    gap: { xs: pxToRem(15), md: pxToRem(0), lg: pxTovW(0) },
  },
};

export const ProjectCongratulations = () => {
  const navigate = useNavigate();
  const { user_info } = deserify(useAppSelector((state) => state.auth));

  const { assessmentId } = useParams();
  const { LmsAssessmentCommonAPIServiceV1ClientWithStatusCodeHandler } =
    useCommonServiceClientContext();

  const dispatch = useAppDispatch();
  const { class_subject_info } = deserify(
    useAppSelector((state) => state.homeDashboard)
  );
  const largeScreen = useMediaQuery(theme.breakpoints.up('md'));
  const { isOffline } = useDownloadContext();
  const printableRef = useRef<any>(null);
  const [createdAssessmentContent, setCreatedAssessmentContent] = useState<
    AssessmentContent | undefined
  >();

  // The assigned project as the server holds it - the only source that survives
  // a reload of this screen, since the flow state is reset on mount.
  const assignedProject = createdAssessmentContent?.assessment;
  const classSection = [assignedProject?.class, assignedProject?.section]
    .filter(Boolean)
    .join('');

  // A project's duration is the time its selected resources add up to.
  const projectDurationInMin = (
    createdAssessmentContent?.projectResources ?? []
  ).reduce((total, resource) => total + resource.estimatedTimeInMin, 0);

  const { setSelectedFunction } = useGlobalContext();

  // -----------------------------------------------------

  const getAssessmentDetails = async () => {
    if (!assessmentId) {
      return;
    }
    try {
      const response =
        await LmsAssessmentCommonAPIServiceV1ClientWithStatusCodeHandler.fetchAssessmentContent(
          {
            personId: user_info?.teacherProfileId,
            personType: ProfileRolesEnum.PROFILE_ROLE_TEACHER,
            assessmentId: BigInt(assessmentId),
          }
        );
      if (response.data) {
        const asmnt = response.data;
        setCreatedAssessmentContent(asmnt);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const backButtonClick = async () => {
    dispatch(resetProjectFlow());
    navigate(HOMEWORK_MANAGE);
  };

  useEffect(() => {
    dispatch(resetProjectFlow());
    setSelectedFunction(() => backButtonClick);
    return () => {
      setSelectedFunction(null);
    };
  }, []);

  useEffect(() => {
    getAssessmentDetails();
  }, [assessmentId]);

  return (
    <Box sx={styles.root}>
      <Box>
        <ImageWrapper
          name="correct-tick"
          parentFolder="icons"
          type="png"
          styles={styles.correctImage}
        />
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          gap: { xs: pxToRem(15), md: pxToRem(4), lg: pxTovW(4) },
          marginTop: { xs: pxToRem(20), md: pxToRem(0), lg: pxTovW(0) },
        }}
      >
        <Typography variant="h1" color="success.main" fontWeight={700}>
          Congratulations!
        </Typography>
        <Typography variant="h2" fontWeight={500} textAlign={'center'}>
          Your Project Successfully Assigned
        </Typography>
      </Box>

      {assignedProject && (
        <Box sx={styles.detailsStrip}>
          <Box sx={styles.detailsColumn}>
            <Typography
              variant={largeScreen ? 'cardText' : 'h3'}
              fontWeight="regular"
            >
              Practical Name:
            </Typography>
            <Typography
              variant={largeScreen ? 'bodyText' : 'h1'}
              color="primary.main"
              fontWeight="bold"
              sx={{
                width: { md: pxToRem(160), lg: pxTovW(200) },
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textAlign: 'center',
                wordBreak: 'break-word',
              }}
            >
              {assignedProject.assessmentTitle}
            </Typography>
          </Box>

          <Box sx={styles.detailsColumn}>
            <Typography
              variant={largeScreen ? 'cardText' : 'h3'}
              fontWeight="regular"
            >
              Deadline:
            </Typography>
            <Typography
              variant={largeScreen ? 'bodyText' : 'h1'}
              color="primary.main"
              textAlign={'center'}
              fontWeight="bold"
            >
              {formatSecondsToDateTimeString(
                assignedProject.assessmentTargetDate?.seconds,
                true
              )}
            </Typography>
          </Box>

          <Box sx={styles.detailsColumn}>
            <Typography
              variant={largeScreen ? 'cardText' : 'h3'}
              fontWeight="regular"
            >
              Class:
            </Typography>
            <Typography
              variant={largeScreen ? 'bodyText' : 'h1'}
              color="primary.main"
              textAlign={'center'}
              fontWeight="bold"
            >
              {classSection} {assignedProject.subject}
            </Typography>
          </Box>

          <Box sx={styles.detailsLastColumn}>
            <Typography
              variant={largeScreen ? 'cardText' : 'h3'}
              fontWeight="regular"
            >
              Duration:
            </Typography>
            <Typography
              variant={largeScreen ? 'bodyText' : 'h1'}
              color="primary.main"
              textAlign={'center'}
              fontWeight="bold"
            >
              {formatDuration(projectDurationInMin)}
            </Typography>
          </Box>
        </Box>
      )}

      {/* Disabled Share for now */}
      {/* <Box
        sx={{
          display: 'flex',
          height: { xs: pxToRem(54), md: pxToRem(39), lg: pxTovW(54) },
          width: { xs: '100vw', md: pxTovW(757) },
          justifyContent: 'center',
          alignItems: 'center',
          border: '2px solid #D8D8FA',
          gap: { xs: pxToRem(20), md: pxToRem(18), lg: pxTovW(20) },
          backgroundColor: '#F8F8FF',
          marginTop: { xs: pxToRem(20), md: pxToRem(17), lg: pxTovW(17) },
          borderRadius: { xs: pxToRem(5), md: pxToRem(5), lg: pxTovW(5) },
        }}
      >
        <Typography variant={largeScreen ? 'cardText' : 'h3'} color="#3F4D8F">
          SHARE:
        </Typography>
        <Box
          sx={{
            display: 'flex',

            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <ImageWrapper
            name="whatsapp"
            parentFolder="icons"
            type="png"
            styles={{
              height: { xs: pxToRem(32), md: pxToRem(26), lg: pxTovW(32) },
              width: { xs: pxToRem(32), md: pxToRem(26), lg: pxTovW(32) },
            }}
          />
          <ImageWrapper
            name="copy-link"
            parentFolder="icons"
            type="png"
            styles={{
              height: { xs: pxToRem(32), md: pxToRem(32), lg: pxTovW(42) },
              width: { xs: pxToRem(32), md: pxToRem(32), lg: pxTovW(42) },
            }}
          />
        </Box>
      </Box> */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          position: { xs: 'fixed', md: 'unset' },
          bottom: { xs: '14px' },
          left: { xs: 0, md: 'unset' },
          width: { xs: '100%', md: 'unset' },
          justifyContent: { xs: 'center', md: 'unset' },
          alignItems: { xs: 'center', md: 'unset' },
          paddingY: { xs: pxToRem(14), md: 0 },
          backgroundColor: { xs: '#FFFFFF', md: 'unset' },
          gap: { xs: pxToRem(20), md: pxToRem(18), lg: pxTovW(20) },
          marginTop: { xs: pxToRem(20), md: pxToRem(17), lg: pxTovW(17) },
        }}
      >
        {/* <SecondaryButton
          styles={{
            backgroundColor: '#E4FFF0',
            height: { xs: pxToRem(55), md: pxToRem(39), lg: pxTovW(55) },
            width: { xs: pxToRem(332), md: pxTovW(350) },
            border: '1px solid #0AA34F75',
            color: '#0AA34F',
            display: { xs: 'none', md: 'block' },
          }}
          onClick={() => navigate('/homework-dashboard')}
        >
          NEXT HOMEWORK
        </SecondaryButton> */}
        <SecondaryButton
          styles={{
            height: { xs: pxToRem(55), md: pxTovW(65), lg: pxTovW(55) },
            width: { xs: pxToRem(332), md: pxTovW(500), lg: pxTovW(350) },
          }}
          onClick={() => {
            navigate(HOMEWORK_MANAGE);
          }}
        >
          <Typography
            variant={largeScreen ? 'h4' : 'h2'}
            color="#FFFFFF"
            fontWeight={700}
          >
            Create New Project
          </Typography>
        </SecondaryButton>
        <SecondaryButton
          styles={{
            height: { xs: pxToRem(55), md: pxTovW(65), lg: pxTovW(55) },
            width: { xs: pxToRem(332), md: pxTovW(500), lg: pxTovW(350) },
          }}
          onClick={() => {
            navigate(Project_Dashboard, {
              state: { fromProjectCongratulations: true },
            });
          }}
        >
          <Typography
            variant={largeScreen ? 'h4' : 'h2'}
            color="#FFFFFF"
            fontWeight={700}
          >
            Go to Project Dashboard
          </Typography>
        </SecondaryButton>
      </Box>
    </Box>
  );
};

/** Project time is stored in minutes; the strip reads better in hours. */
function formatDuration(minutes?: number) {
  if (!minutes) {
    return 'Nil';
  }
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = Math.round(minutes % 60);
  if (hours === 0) {
    return `${remainingMinutes} min`;
  }
  const hoursLabel = `${hours} ${hours === 1 ? 'hr' : 'hrs'}`;
  return remainingMinutes === 0
    ? hoursLabel
    : `${hoursLabel} ${remainingMinutes} min`;
}

function formatDate(inputDate?: string) {
  const options: any = {
    year: 'numeric',
    month: 'long',
    day: '2-digit',
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
  };
  if (!inputDate) {
    return 'No date';
  }
  const date = new Date(inputDate);
  const formattedDate = date.toLocaleDateString('en-US', options);

  // Extract day and month
  const day = date.getDate();
  const month = date.toLocaleDateString('en-US', { month: 'long' });

  // Format day with ordinal suffix
  const ordinalSuffix = getOrdinalSuffix(day);
  const formattedDay = `${day}${ordinalSuffix}`;

  // Split formattedDate to separate date and time
  const [formattedTime, amPm] = formattedDate.split(' ');

  return `${formattedDay} ${month}, ${formattedTime}${amPm}`;
}

function getOrdinalSuffix(number: number) {
  const suffixes = ['th', 'st', 'nd', 'rd'];
  const lastDigit = number % 10;
  const suffix =
    lastDigit <= 3 && (number < 11 || number > 13)
      ? suffixes[lastDigit]
      : suffixes[0];
  return suffix;
}
const getAssessmentName = (assessType?: AssessmentEnum) => {
  if (assessType) {
    switch (assessType) {
      case AssessmentEnum.ASSESSMENT_DIAGNOSTIC_ASSESSMENT:
        return 'Diagnostic Assessment';
      case AssessmentEnum.ASSESSMENT_FORMATIVE_ASSESSMENT:
        return 'Formative Assessment';
      case AssessmentEnum.ASSESSMENT_HOMEWORK:
        return 'Homework';
      case AssessmentEnum.ASSESSMENT_PROJECT:
        return 'Projec';
      default:
        return 'Assessment';
    }
  } else {
    return 'Assessment';
  }
};
